import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';

import { formatDate } from 'components/helpers/dates';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function DestroyBookingSourceModal({
  bookingSource,
  course,
  isOpen,
  onClose,
  onDestroySubmit,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.DestroyBookingSourceModal',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const isBookingGroup = bookingSource.type === 'bookingGroup';
  const personnelCount = isBookingGroup && bookingSource.meta.personnelCount;

  const courseHasELearningCourse =
    !!course?.relationships?.eLearningCourse?.data;

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onClose}
            size='md'
          >
            {i18nTCommon('shared.cancel')}
          </TextButton>
          <FilledButton
            color='red'
            onClick={() => onDestroySubmit({ bookingSource, course })}
          >
            {isBookingGroup ?
              i18nT('bookingGroup.button', { count: personnelCount })
            : courseHasELearningCourse ?
              i18nTCommon('bookings.removeEnrolment')
            : i18nTCommon('bookings.removeBooking')}
          </FilledButton>
        </div>
      }
      isOpen={isOpen}
      mode='flexible'
      onClose={onClose}
    >
      <div className='tw-p-6'>
        <h2
          className={classNames(
            'tw-m-0 tw-p-0 tw-text-l tw-font-semibold tw-text-grey-900',
            isBookingGroup && 'tw-mb-6',
          )}
        >
          {isBookingGroup ?
            i18nT(
              `bookingGroup.title.${courseHasELearningCourse ? 'eLearning' : 'default'}`,
              { count: personnelCount },
            )
          : i18nTCommon(
              courseHasELearningCourse ?
                'bookings.areYouSureYouWantToRemoveEnrolmentForCourse'
              : 'bookings.areYouSureYouWantToRemoveBookingForCourse',
              {
                courseName: course?.attributes.name,
                interpolation: { escapeValue: false },
              },
            )
          }
        </h2>
        {isBookingGroup && (
          <>
            <p className='tw-m-0 tw-p-0 tw-text-m tw-font-normal tw-text-grey-700'>
              {`${i18nTCommon('shared.course')}: `}
              <span className='tw-m-0 tw-p-0 tw-text-m tw-font-medium tw-text-grey-700'>
                {course.attributes.name}
              </span>
            </p>
            <p className='tw-m-0 tw-p-0 tw-text-m tw-font-normal tw-text-grey-700'>
              {`${i18nTCommon('training.trainingDate')}: `}
              <span className='tw-m-0 tw-p-0 tw-text-m tw-font-medium tw-text-grey-700'>
                {formatDate({
                  date: bookingSource.attributes.date,
                  format: 'd MMM yyyy',
                })}
              </span>
            </p>
          </>
        )}
        <br />
        <p className='tw-m-0 tw-p-0 tw-text-m tw-font-normal tw-text-grey-700'>
          {isBookingGroup ?
            i18nT('bookingGroup.description', { count: personnelCount })
          : i18nTCommon('personnel.emailNotifications.personnelAndLineManager')}
        </p>
      </div>
    </ModalBox>
  );
}

DestroyBookingSourceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bookingSource: resourceShape(['booking', 'bookingGroup']).isRequired,
  course: resourceShape('course').isRequired,
  onDestroySubmit: PropTypes.func.isRequired,
};
